import React, { useCallback, useEffect } from 'react';
import { usePusher } from '@/App';
import { MyAppointments as MyAppointmentsAction } from '@/actions';
import LoadingV2 from '@/components/Loading';
import { TableV3 } from '@/components/TableV3';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

const ToCancelAppointments = ({
  columns,
  containerName,
  orderBy,
  setOrderBy,
  handleExpandRow,
  handleResponse,
  relatedAppointments,
  loadingRelated,
  expandedRow,
  filtersLoaded,
  selectedRowForLinks,
  paramKey = 'only_cancelable',
}) => {
  const { channels } = usePusher();

  const fetchApi = useCallback(
    (page) => {
      if (!filtersLoaded) {
        return Promise.resolve({ data: { appointments: { data: [] } } });
      }

      return MyAppointmentsAction({
        container_name: containerName,
        page: page,
        per_page: 15,
        order_by: orderBy,
        [paramKey]: 1,
      });
    },
    [orderBy, containerName, filtersLoaded, paramKey]
  );

  const {
    items: DATATable,
    loading: loadingData,
    refetch,
  } = useInfiniteScroll(fetchApi, {
    deps: filtersLoaded ? [containerName, orderBy, paramKey] : [],
    searchText: containerName,
    responseDataKey: 'appointments',
    handleResponse,
  });

  const isNearTopOrFirstPage = useCallback(() => {
    const mainEl = document.getElementsByTagName('main')[0];
    const scrollTop = mainEl ? mainEl.scrollTop : window.scrollY || 0;
    const nearTop = scrollTop <= 50; // threshold

    return nearTop;
  }, [DATATable?.length]);

  useEffect(() => {
    if (channels?.privateChannel) {
      channels.privateChannel.bind('ContainerAppointmentUpdate2', () => {
        // Auto-refresh data only if the user is near the top/first page.
        if (isNearTopOrFirstPage()) {
          refetch();
        }
      });

      return () => {
        channels.privateChannel.unbind('ContainerAppointmentUpdate2');
      };
    }
  }, [channels, fetchApi, isNearTopOrFirstPage, refetch]);

  const handleOrderBy = (orderBy) => {
    setOrderBy(orderBy);

    // Clear localStorage if orderBy is undefined/null, otherwise save it
    if (!orderBy) {
      localStorage.removeItem(`appointments-${paramKey}-orderBy`);
    } else {
      localStorage.setItem(`appointments-${paramKey}-orderBy`, orderBy);
    }
  };

  return (
    <>
      {!(loadingData && DATATable.length === 0) && (
        <div className="mb-4">
          <TableV3
            isExpandable
            headers={columns}
            data={DATATable || []}
            emptyMessage=""
            minWidth="1510px"
            stickyHeader={true}
            appHeaderHeight={64}
            onSort={handleOrderBy}
            currentSortKey={orderBy}
            expandedRow={expandedRow}
            relatedAppointments={relatedAppointments}
            loadingRelated={loadingRelated}
            onExpandRow={handleExpandRow}
            localStorageKey={`${paramKey}-appointments-selected-row`}
            getRowClassName={(item) =>
              selectedRowForLinks?.id === item.id ? 'z-99' : 'z-9'
            }
            selectedRowForLinks={selectedRowForLinks}
          />
        </div>
      )}

      {loadingData &&
        (DATATable.length === 0 || (DATATable && DATATable.length > 0)) && (
          <div className="flex justify-center py-4">
            <LoadingV2 />
          </div>
        )}
    </>
  );
};

export default ToCancelAppointments;
